import { SizingCalculatorAttributesType } from 'data/types';
import { numericOpeningPosition } from 'utils/opening-position-value';

type OpeningLikeType = {
  uniqueId?: string;
  positionOnWall?: unknown;
};

type WallWithOpeningsType = {
  verticalGlass?: OpeningLikeType[];
  solidDoors?: OpeningLikeType[];
};

type StructureSpaceLookupType = Array<{
  sections: Array<{
    spaces: Array<{
      uniqueId: string;
      sizingAttributes?: SizingCalculatorAttributesType;
    }>;
  }>;
}>;

function indexOpeningPositions(
  walls: WallWithOpeningsType[] | undefined
): Map<string, number> {
  const byId = new Map<string, number>();

  walls?.forEach((wall) => {
    const openings = [
      ...(wall.verticalGlass ?? []),
      ...(wall.solidDoors ?? [])
    ];
    openings.forEach((item) => {
      const position = numericOpeningPosition(item.positionOnWall);
      if (!item.uniqueId || position === undefined) {
        return;
      }
      byId.set(item.uniqueId, position);
    });
  });

  return byId;
}

function restoreOpeningPosition<T extends OpeningLikeType>(
  item: T,
  positions: Map<string, number>
): T {
  if (numericOpeningPosition(item.positionOnWall) !== undefined) {
    return item;
  }

  const stored = item.uniqueId ? positions.get(item.uniqueId) : undefined;
  if (stored === undefined) {
    return item;
  }

  return {
    ...item,
    positionOnWall: stored
  };
}

function applyOpeningPositions<T extends WallWithOpeningsType>(
  walls: T[] | undefined,
  positions: Map<string, number>
): T[] | undefined {
  if (!walls) {
    return walls;
  }

  return walls.map((wall) => ({
    ...wall,
    verticalGlass: wall.verticalGlass?.map((item) =>
      restoreOpeningPosition(item, positions)
    ),
    solidDoors: wall.solidDoors?.map((item) =>
      restoreOpeningPosition(item, positions)
    )
  }));
}

export function mergeOpeningPositionsFromLocal(
  local: SizingCalculatorAttributesType | undefined,
  remote: SizingCalculatorAttributesType | undefined
): SizingCalculatorAttributesType | undefined {
  if (!remote || !local) {
    return remote;
  }

  const positions = new Map([
    ...indexOpeningPositions(local.aboveGradeWalls),
    ...indexOpeningPositions(local.belowGradeWalls)
  ]);

  if (positions.size === 0) {
    return remote;
  }

  return {
    ...remote,
    aboveGradeWalls:
      applyOpeningPositions(remote.aboveGradeWalls, positions) ??
      remote.aboveGradeWalls,
    belowGradeWalls:
      applyOpeningPositions(remote.belowGradeWalls, positions) ??
      remote.belowGradeWalls
  };
}

export function mergeRemoteSpaceOpeningPositions(
  spaceUniqueId: string,
  remote: SizingCalculatorAttributesType | undefined,
  structure: StructureSpaceLookupType
): SizingCalculatorAttributesType | undefined {
  const local = structure
    .flatMap((level) => level.sections)
    .flatMap((section) => section.spaces)
    .find((space) => space.uniqueId === spaceUniqueId)?.sizingAttributes;

  return mergeOpeningPositionsFromLocal(local, remote);
}
